import { useEffect, useRef, useState } from "react";
import api, { fileUrl, API } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import MembershipCard from "@/components/MembershipCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "mesh", label: "Gradient Mesh" },
  { key: "minimal", label: "Minimalist Mono" },
  { key: "botanical", label: "Botanical" },
  { key: "luxe", label: "Dark Luxe" },
];

function TierCardUpload({ tier, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/admin/tiers/${tier.id}/card-image`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded for ${tier.name}`);
      await onChange();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const removeImg = async () => {
    try {
      await api.delete(`/admin/tiers/${tier.id}/card-image`);
      toast.success("Image removed");
      await onChange();
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="bg-white border border-mr rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ background: tier.color }} />
          <p className="font-display font-semibold text-mr-primary">{tier.name}</p>
          <span className="label-eyebrow text-mr-secondary">{tier.threshold.toLocaleString()}+ pts</span>
        </div>
      </div>
      <div className="aspect-[1.586/1] rounded-xl overflow-hidden border border-mr bg-mr-bg flex items-center justify-center">
        {tier.card_image_path ? (
          <img src={fileUrl(tier.card_image_path)} alt={tier.name} className="w-full h-full object-cover" />
        ) : (
          <p className="text-mr-secondary text-xs">No custom image — uses default template</p>
        )}
      </div>
      <input data-testid={`tier-card-input-${tier.id}`} ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} />
      <div className="mt-3 flex gap-2">
        <Button data-testid={`tier-card-upload-${tier.id}`} disabled={busy} variant="outline" size="sm" className="rounded-full flex-1" onClick={() => inputRef.current?.click()}>
          <Upload className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Uploading…" : tier.card_image_path ? "Replace" : "Upload image"}
        </Button>
        {tier.card_image_path && (
          <Button data-testid={`tier-card-remove-${tier.id}`} variant="ghost" size="sm" className="rounded-full" onClick={removeImg}>
            <Trash2 className="h-3.5 w-3.5 text-mr-accent" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminBranding() {
  const [tenant, setTenant] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    const [t, ti] = await Promise.all([api.get("/admin/branding"), api.get("/admin/tiers")]);
    setTenant(t.data);
    setTiers(ti.data);
  };
  useEffect(() => { load(); }, []);

  const updateField = (k, v) => setTenant({ ...tenant, [k]: v });

  const save = async () => {
    try {
      await api.put("/admin/branding", {
        name: tenant.name,
        brand_color: tenant.brand_color,
        accent_color: tenant.accent_color,
        card_template: tenant.card_template,
      });
      toast.success("Branding saved");
      await load();
    } catch { toast.error("Failed to save"); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post("/admin/branding/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); fileInput.current.value = ""; }
  };

  const downloadCardTemplate = async () => {
    try {
      const resp = await api.get("/admin/card-template.png", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url; a.download = "medrewards_card_template.png";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Could not download template"); }
  };

  if (!tenant) return <AdminShell title="Branding"><div /></AdminShell>;

  // For the preview, find a representative tier (Gold if exists, else first)
  const previewTier = tiers.find((t) => t.name === "Gold") || tiers[0];

  return (
    <AdminShell title="Branding">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="bg-white border border-mr rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold text-mr-primary mb-4">Identity</h2>
            <div className="space-y-4">
              <div><Label>Clinic name</Label><Input data-testid="brand-name" value={tenant.name} onChange={(e) => updateField("name", e.target.value)} className="mt-2" /></div>
              <div className="flex items-end gap-4">
                <div className="flex-1"><Label>Brand color</Label><Input data-testid="brand-primary" type="color" value={tenant.brand_color} onChange={(e) => updateField("brand_color", e.target.value)} className="mt-2 h-11" /></div>
                <div className="flex-1"><Label>Accent color</Label><Input data-testid="brand-accent" type="color" value={tenant.accent_color} onChange={(e) => updateField("accent_color", e.target.value)} className="mt-2 h-11" /></div>
              </div>
              <div>
                <Label>Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  {tenant.logo_path ? (
                    <img src={fileUrl(tenant.logo_path)} alt="logo" className="h-16 w-16 object-cover rounded-xl border border-mr" />
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-mr-bg border border-mr flex items-center justify-center text-mr-secondary text-xs">No logo</div>
                  )}
                  <input data-testid="brand-logo-input" ref={fileInput} type="file" accept="image/*" onChange={onUpload} className="hidden" />
                  <Button data-testid="brand-logo-upload-btn" disabled={uploading} variant="outline" className="rounded-full" onClick={() => fileInput.current.click()}>
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload logo"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-mr rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold text-mr-primary mb-2">Default card template</h2>
            <p className="text-mr-secondary text-sm mb-4">Used for any tier that doesn't have a custom image (below).</p>
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`brand-template-${t.key}`}
                  onClick={() => updateField("card_template", t.key)}
                  className={`p-4 rounded-xl border text-left transition-all ${tenant.card_template === t.key ? "border-mr-accent ring-2 ring-mr-accent/30" : "border-mr"}`}
                >
                  <p className="font-display font-semibold text-mr-primary">{t.label}</p>
                  <p className="text-xs text-mr-secondary mt-1">Tap to apply</p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button data-testid="brand-save-btn" onClick={save} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Save className="h-4 w-4 mr-2" /> Save branding</Button>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 self-start">
          <p className="label-eyebrow text-mr-secondary mb-3">Live preview · {previewTier?.name || "Standard"}</p>
          <MembershipCard
            tenant={tenant}
            user={{ name: "Amir Khalifa", tier: previewTier?.name || "Standard", id: "demo-preview-id" }}
            variant={tenant.card_template}
            customImagePath={previewTier?.card_image_path}
          />
        </div>
      </div>

      {/* Per-tier custom card designs */}
      <div className="mt-10 bg-white border border-mr rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h2 className="font-display text-xl font-semibold text-mr-primary">Custom card designs per tier</h2>
            <p className="text-mr-secondary text-sm mt-1 max-w-2xl">Upload a unique background image for each tier — e.g. an elegant dark image for VIP, a brighter design for Gold. Download the PNG template to see safe zones, then design and re-upload.</p>
          </div>
          <Button data-testid="brand-download-template-btn" variant="outline" className="rounded-full" onClick={downloadCardTemplate}>
            <Download className="h-4 w-4 mr-2" /> Download PNG template
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiers.map((t) => <TierCardUpload key={t.id} tier={t} onChange={load} />)}
        </div>
      </div>
    </AdminShell>
  );
}
